import React, { useEffect, useMemo, useState } from "react";
import {
  MapPin,
  Loader2,
  Check,
  ChevronRight,
  FileText,
} from "lucide-react";
import { decryptData } from "../utils/storageCrypto";
import PackageIcon from "../assets/icons/Package.svg";
import GoogleRoutePreview from "./components/GoogleRoutePreview";
import GeofenceBottomSheetModal from "./components/GeofenceBottomSheetModal";

/* -------------------- PHASE DEFINITIONS -------------------- */
const PHASES = {
  NAVIGATE: "navigate",         // Screen 2: Navigate to Pickup
  VERIFY_ITEMS: "verify_items", // Screen 4: Checklist & Counter Pass
  ENTER_OTP: "enter_otp",       // Screen 5: Pickup OTP
  COMPLETED: "completed",       // Screen 6: Pickup Completed
};

const ActivePickupStep = ({
  step,
  totalStepsCount,
  onComplete,
  activeRide,
  isLastStep,
}) => {
  const stopStorageKey = `pickup_phase_${activeRide?.tripId || "active"}_${step?.stop_id || step?.step || 1}`;
  const [phase, setPhase] = useState(() => {
    const saved = sessionStorage.getItem(stopStorageKey);
    return saved || PHASES.NAVIGATE;
  });

  const [otpValue, setOtpValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showGeofenceModal, setShowGeofenceModal] = useState(false);
  const [optionalNote, setOptionalNote] = useState("");
  const [verifiedMap, setVerifiedMap] = useState({});

  const itemsList = useMemo(() => step?.items || [], [step]);

  /* Calculate milestone label (P1, P2...) */
  const stepMilestoneLabel = useMemo(() => {
    if (!activeRide?.steps?.length) return `P${step?.step || 1}`;
    let count = 0;
    for (const s of activeRide.steps) {
      if (s.type === "PICKUP") count += 1;
      if (s.step === step?.step || s.po_id === step?.po_id || s.stop_id === step?.stop_id) break;
    }
    return `P${count || 1}`;
  }, [activeRide, step]);

  /* Calculate stop index out of total (e.g. 1 OF 4) */
  const currentStopIndex = useMemo(() => {
    if (!activeRide?.steps?.length) return 1;
    const idx = activeRide.steps.findIndex(
      (s) => s.stop_id === step?.stop_id || s.step === step?.step
    );
    return idx >= 0 ? idx + 1 : 1;
  }, [activeRide, step]);

  /* Next step calculation for Screen 6 preview */
  const nextStepInfo = useMemo(() => {
    if (!activeRide?.steps?.length) return null;
    const curIdx = activeRide.steps.findIndex(
      (s) => s.stop_id === step?.stop_id || s.step === step?.step
    );
    if (curIdx >= 0 && curIdx < activeRide.steps.length - 1) {
      const nextS = activeRide.steps[curIdx + 1];
      const isPick = nextS.type === "PICKUP";
      return {
        label: isPick ? "Pickup" : "Drop-off",
        title: `${isPick ? "Pickup" : "Drop-off"} - ${nextS.name || nextS.area || "Next Stop"}`,
        distance: nextS.distance || "5.1 km",
        time: "14 min",
      };
    }
    return null;
  }, [activeRide, step]);

  useEffect(() => {
    if (!step) return;
    const currentStatus = step.status ? String(step.status).toUpperCase() : "";

    if (currentStatus === "COMPLETED" || currentStatus === "SKIPPED") {
      setPhase(PHASES.COMPLETED);
    } else if (currentStatus === "OTP_GENERATED") {
      setPhase(PHASES.ENTER_OTP);
    }
  }, [step]);

  const updatePhase = (newPhase) => {
    sessionStorage.setItem(stopStorageKey, newPhase);
    setPhase(newPhase);
  };

  /* Screen 2 -> Reached Pickup Point Trigger */
  const handleReachedClick = () => {
    setShowGeofenceModal(true);
  };

  /* Screen 3a -> Collect Items Trigger */
  const handleProceedToVerifyItems = () => {
    setShowGeofenceModal(false);
    updatePhase(PHASES.VERIFY_ITEMS);
  };

  /* Item checklist toggle */
  const toggleItemVerified = (key) => {
    setVerifiedMap((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  /* Screen 4 -> Generate Stop OTP */
  const handleProceedToOTP = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const URL = process.env.REACT_APP_FORE_BRICKBUCKET;
      const encryptedToken = sessionStorage.getItem("token");
      const token = decryptData(encryptedToken);

      const res = await fetch(`${URL}delivery/generate-stop-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-token": token,
        },
        body: JSON.stringify({
          leg_id: step.leg_id,
          stop_id: step.stop_id,
          trip_id: activeRide.tripId,
        }),
      });

      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Failed to generate OTP");

      updatePhase(PHASES.ENTER_OTP);
    } catch (err) {
      setErrorMessage(err.message || "Failed to generate OTP");
      updatePhase(PHASES.ENTER_OTP);
    } finally {
      setLoading(false);
    }
  };

  /* Verify OTP & Show Completion */
  const handleVerifyOTP = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const URL = process.env.REACT_APP_FORE_BRICKBUCKET;
      const encryptedToken = sessionStorage.getItem("token");
      const token = decryptData(encryptedToken);

      const res = await fetch(`${URL}delivery/verify-stop-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-token": token,
        },
        body: JSON.stringify({
          trip_id: activeRide.tripId,
          stop_id: step.stop_id,
          otp: Number(otpValue),
          leg_id: step.leg_id,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || "OTP verification failed");

      sessionStorage.removeItem(stopStorageKey);
      updatePhase(PHASES.COMPLETED);
    } catch (err) {
      setErrorMessage(err.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3.5 pb-4">
      {/* ========================================================================= */}
      {/* SCREEN 2: NAVIGATE TO PICKUP                                             */}
      {/* ========================================================================= */}
      {phase === PHASES.NAVIGATE && (
        <div className="space-y-3 animate-fadeIn">
          {/* Destination Details Card */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-xs">
            <div className="bg-gray-50 border-b border-gray-100 p-3.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-black uppercase tracking-wider text-red-600 font-display">
                  CURRENT STOP • {currentStopIndex} OF {totalStepsCount}
                </span>
                <span className="text-[10px] font-bold text-amber-700 bg-amber-100/70 px-2.5 py-0.5 rounded-full border border-amber-200 font-mono">
                  Trip in progress
                </span>
              </div>
              <h2
                className="text-xl font-black text-gray-900 leading-tight mt-1"
                style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
              >
                {step?.name || `Pickup ${stepMilestoneLabel}`}
              </h2>
            </div>

            <div className="p-3.5 space-y-2.5">
              <div className="flex items-start gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5" />
                <p className="text-xs text-gray-600 leading-snug" style={{ fontFamily: "DMSans-Regular, sans-serif" }}>
                  {step?.address || "Address not specified"}
                </p>
              </div>
              <p className="text-[11px] font-mono font-bold text-gray-400 pl-5">
                Ref: {step?.refNumber || "PO-000203"}
              </p>

              <div className="flex items-center gap-4 pt-2 border-t border-gray-100 text-xs font-mono font-bold text-gray-600">
                <span>📍 {step?.distance || "2.7 km"}</span>
                <span>⏱️ {step?.estTime || "8 min (est.)"}</span>
              </div>
            </div>
          </div>

          {/* Google Route Preview Card */}
          <GoogleRoutePreview
            destinationLat={step?.latitude}
            destinationLng={step?.longitude}
            destinationAddress={step?.address}
            destinationName={step?.name || "Pickup Point"}
          />

          {/* Primary Action Button */}
          <div className="pt-1">
            <button
              type="button"
              onClick={handleReachedClick}
              className="w-full h-14 bg-white hover:bg-gray-50 active:scale-[0.99] border-2 border-red-600 text-red-600 rounded-2xl flex items-center justify-center gap-2 text-base font-black uppercase tracking-wider transition shadow-sm cursor-pointer"
              style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
            >
              <span>📍</span>
              <span>Reached Pickup Point</span>
            </button>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCREEN 3: PICKUP GEOFENCE VALIDATION BOTTOM SHEET (PRODUCTION SECURE GPS) */}
      {/* ========================================================================= */}
      <GeofenceBottomSheetModal
        isOpen={showGeofenceModal}
        onClose={() => setShowGeofenceModal(false)}
        onProceed={handleProceedToVerifyItems}
        destinationLat={step?.latitude}
        destinationLng={step?.longitude}
        isPickup={true}
        thresholdMeters={100}
      />

      {/* ========================================================================= */}
      {/* SCREEN 4: VERIFY PICKUP ITEMS                                            */}
      {/* ========================================================================= */}
      {phase === PHASES.VERIFY_ITEMS && (
        <div className="space-y-3.5 animate-fadeIn">
          {/* Dispatch Counter Pass Card */}
          <div className="bg-[#FEF3C7] border border-[#FCD34D] rounded-2xl p-4 shadow-xs space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FDE68A] border border-[#F59E0B]/30 flex items-center justify-center shrink-0 shadow-2xs overflow-hidden p-2">
                <img
                  src={PackageIcon}
                  alt="Package"
                  className="w-full h-full object-contain"
                />
              </div>

              <div className="min-w-0">
                <h3
                  className="text-[13px] font-black uppercase tracking-wider text-[#451A03] leading-tight"
                  style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
                >
                  DISPATCH COUNTER PASS
                </h3>
                <p
                  className="text-[11px] font-medium text-[#92400E] leading-snug mt-0.5"
                  style={{ fontFamily: "DMSans-Regular, sans-serif" }}
                >
                  Show this order reference to party
                </p>
              </div>
            </div>

            <div className="w-full bg-[#0B1220] rounded-xl py-2.5 px-4 text-center shadow-inner flex items-center justify-center">
              <span
                className="text-xl sm:text-2xl font-black tracking-widest text-white font-mono"
                style={{ letterSpacing: "0.18em" }}
              >
                {step?.refNumber || "PO-000203"}
              </span>
            </div>
          </div>

          {/* Items to Collect Header & List */}
          <div className="space-y-2.5">
            <h3
              className="text-xs font-black uppercase tracking-wider text-gray-900 px-1"
              style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
            >
              Items to Collect ({itemsList.length})
            </h3>

            {itemsList.map((item, idx) => {
              const itemKey = item.itemId || item.item_id || idx;
              const isChecked = !!verifiedMap[itemKey];
              const qty = Number(item.quantity) || 1;
              const uom = item.uom || item.UOM || "Unit";
              const imageUrl =
                item.item_image || item.image
                  ? `${process.env.REACT_APP_IMAGE_URL || ""}${item.item_image || item.image}`
                  : null;

              return (
                <div
                  key={itemKey}
                  className="bg-white border rounded-2xl p-3 shadow-2xs flex items-center justify-between gap-2 transition"
                  style={{ borderColor: isChecked ? "#10B981" : "#E2E8F0" }}
                >
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0 overflow-hidden">
                      {imageUrl ? (
                        <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <img src={PackageIcon} alt="" className="w-5 h-5 opacity-40" />
                      )}
                    </div>
                    <div className="min-w-0 pr-1">
                      <h4
                        className="text-xs font-bold text-gray-900 leading-snug line-clamp-2"
                        style={{ fontFamily: "DMSans-Medium, sans-serif" }}
                      >
                        {item.name || item.item_name}
                      </h4>
                    </div>
                  </div>

                  <div className="flex items-center gap-2.5 shrink-0">
                    <div className="px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-lg text-center min-w-[42px] flex flex-col items-center justify-center">
                      <span className="text-xs font-mono font-black text-gray-900 leading-none">
                        {qty}
                      </span>
                      <span className="text-[9px] font-mono text-gray-400 uppercase tracking-tight mt-0.5 leading-none">
                        {uom}
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() => toggleItemVerified(itemKey)}
                      className={`w-7 h-7 rounded-full flex items-center justify-center transition active:scale-90 cursor-pointer ${
                        isChecked
                          ? "bg-emerald-600 text-white"
                          : "border-2 border-gray-300 text-transparent"
                      }`}
                    >
                      <Check className="w-4 h-4 stroke-[3]" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Optional Note */}
          <div className="bg-white rounded-xl border border-gray-200 p-2.5 flex items-center gap-2 text-xs text-gray-400">
            <FileText className="w-4 h-4 text-gray-400 shrink-0" />
            <input
              type="text"
              value={optionalNote}
              onChange={(e) => setOptionalNote(e.target.value)}
              placeholder="Add a note (optional)"
              className="w-full bg-transparent outline-none text-gray-800 text-xs placeholder-gray-400"
            />
          </div>

          {/* Bottom CTA to Trigger OTP */}
          <div className="pt-2">
            <button
              type="button"
              disabled={loading}
              onClick={handleProceedToOTP}
              className="w-full h-14 bg-red-600 hover:bg-red-700 active:scale-[0.98] text-white font-black text-sm uppercase tracking-wider rounded-2xl transition shadow-md flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
              style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-white" />
                  <span>Generating Stop OTP...</span>
                </>
              ) : (
                <span>Proceed to Pickup OTP</span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCREEN 5: ENTER PICKUP OTP                                               */}
      {/* ========================================================================= */}
      {phase === PHASES.ENTER_OTP && (
        <div className="space-y-4 animate-fadeIn">
          <div className="bg-white rounded-3xl border border-gray-200 p-5 shadow-xs space-y-5">
            <div className="flex items-center justify-between pb-1">
              <div className="flex items-center gap-2">
                <img
                  src="/iconwithtext.png"
                  alt="Brickbucket"
                  className="h-6 w-auto object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </div>
              <span className="text-[10px] font-black uppercase tracking-wider text-red-600 bg-red-50 border border-red-200 px-3 py-1 rounded-full font-mono">
                PICKUP
              </span>
            </div>

            <div className="bg-gray-50/80 border border-gray-200/80 rounded-2xl p-3.5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center shrink-0 shadow-2xs text-gray-700">
                <svg
                  className="w-5 h-5 text-gray-700"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                  <rect width="20" height="14" x="2" y="6" rx="2" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-gray-400 leading-tight">
                  Order Reference
                </p>
                <h4 className="text-base font-black text-gray-900 tracking-wide font-mono mt-0.5">
                  {step?.refNumber || "PO-000203"}
                </h4>
              </div>
            </div>

            <div className="space-y-1 text-left pt-1">
              <h2
                className="text-lg font-black text-[#E9001E] leading-tight"
                style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
              >
                Enter 4-digit Pickup OTP
              </h2>
              <p
                className="text-xs text-gray-500 leading-snug"
                style={{ fontFamily: "DMSans-Regular, sans-serif" }}
              >
                Ask the supplier or counter person for the code.
              </p>
            </div>

            <div className="relative">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                autoFocus
                value={otpValue}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, "");
                  if (val.length <= 4) setOtpValue(val);
                }}
                className="absolute inset-0 w-full h-full opacity-0 z-20 cursor-pointer"
              />

              <div className="grid grid-cols-4 gap-3 py-1">
                {[0, 1, 2, 3].map((index) => {
                  const digit = otpValue[index] || "";
                  const isCurrent = otpValue.length === index;

                  return (
                    <div
                      key={index}
                      className={`h-16 rounded-2xl flex items-center justify-center text-2xl font-black font-mono transition-all duration-150 ${
                        digit
                          ? "bg-white border-2 border-slate-300 text-gray-900 shadow-xs"
                          : isCurrent
                          ? "bg-white border-2 border-[#E9001E] shadow-sm ring-2 ring-red-100"
                          : "bg-gray-50/60 border border-gray-200 text-transparent"
                      }`}
                    >
                      {digit}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="pt-1">
              <a
                href="tel:+918247895454"
                className="inline-flex items-center justify-between w-full text-xs font-bold text-gray-600 hover:text-gray-900 transition py-1"
                style={{ fontFamily: "DMSans-Medium, sans-serif" }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-red-50 flex items-center justify-center text-red-600">
                    <svg
                      className="w-3.5 h-3.5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  </div>
                  <span>Can't get OTP? Contact Support</span>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </a>
            </div>
          </div>

          {errorMessage && (
            <div className="p-3 rounded-2xl border border-red-200 bg-red-50 text-red-600 text-xs font-medium text-center">
              {errorMessage}
            </div>
          )}

          <button
            type="button"
            disabled={otpValue.length !== 4 || loading}
            onClick={handleVerifyOTP}
            className="w-full h-14 bg-[#E9001E] hover:bg-red-700 active:scale-[0.98] text-white font-black text-sm uppercase tracking-wider rounded-2xl transition shadow-md flex items-center justify-center gap-2 cursor-pointer disabled:opacity-40"
            style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin text-white" />
                <span>Verifying...</span>
              </>
            ) : (
              <span>Verify OTP & Complete Pickup</span>
            )}
          </button>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCREEN 6: PICKUP COMPLETED CELEBRATION                                   */}
      {/* ========================================================================= */}
      {phase === PHASES.COMPLETED && (
        <div className="space-y-4 animate-fadeIn text-center py-2">
          <div className="w-20 h-20 rounded-full bg-emerald-500 text-white flex items-center justify-center mx-auto shadow-lg animate-scaleUp">
            <Check className="w-10 h-10 stroke-[3]" />
          </div>

          <div className="space-y-1">
            <h2
              className="text-2xl font-black text-gray-900"
              style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
            >
              Pickup {stepMilestoneLabel} Completed!
            </h2>
            <p className="text-xs text-gray-500">
              {itemsList.length} items collected from {step?.name || "Pickup Point"}.
            </p>
          </div>

          {/* Static Informational Next Stop Card (Non-clickable, read-only) */}
          {nextStepInfo && (
            <div className="bg-white rounded-2xl border border-gray-200 p-3.5 shadow-xs flex items-center text-left select-none pointer-events-none">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-red-600 text-white flex items-center justify-center font-bold text-xs shrink-0">
                  {nextStepInfo.label === "Pickup" ? "P2" : "D1"}
                </div>
                <div>
                  <span className="text-[10px] font-black uppercase text-gray-400 tracking-wider">
                    Next Stop
                  </span>
                  <h4 className="text-xs font-black text-gray-900 leading-tight">
                    {nextStepInfo.title}
                  </h4>
                  <p className="text-[11px] font-mono text-gray-400 mt-0.5">
                    {nextStepInfo.distance} • {nextStepInfo.time}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Primary Action Button */}
          <div className="pt-2">
            <button
              type="button"
              onClick={() => {
                sessionStorage.removeItem(stopStorageKey);
                onComplete?.();
              }}
              className="w-full h-14 bg-red-600 hover:bg-red-700 active:scale-[0.98] text-white font-black text-sm uppercase tracking-wider rounded-2xl transition shadow-md cursor-pointer"
              style={{ fontFamily: "BarlowCondensed-Bold, sans-serif" }}
            >
              <span>{isLastStep ? "Complete Trip" : `Navigate to ${nextStepInfo?.label || "Next Stop"}`}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivePickupStep;